import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { ApiFactory } from '../api/easypos-api';
import { IZReport } from '../api/model/requests';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { styles as sharedStyles } from '../styles/shared-styles';
import { designPrimitives } from '../styles/design-primitives';
import { router as appRouter } from '../router';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

@customElement('cash-z-report-page')
export class CashZReportPage extends Base {
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _report: IZReport | null = null;

  static styles = [
    sharedStyles,
    designPrimitives,
    css`
      :host {
        display: block;
        max-width: 900px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      .report-head {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        margin-bottom: 18px;
      }
      .report-head .icon-tile {
        margin-top: 2px;
      }
      .sub {
        color: var(--s-ink-soft, #666);
        margin: 0 0 16px;
        font-size: 0.9rem;
        display: flex;
        align-items: center;
        gap: 6px 10px;
        flex-wrap: wrap;
      }
      .panel {
        margin-bottom: 14px;
      }
      .panel h3 {
        margin: 0 0 10px;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--s-ink-soft, #666);
      }
      .balance-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
        font-variant-numeric: tabular-nums;
      }
      @media (min-width: 720px) {
        .balance-grid {
          grid-template-columns: repeat(4, 1fr);
        }
      }
      .cell {
        background: var(--s-canvas-soft, #f7f7f7);
        border: 1px solid transparent;
        border-radius: 12px;
        padding: 10px 12px;
      }
      .cell.strong {
        background: var(--s-canvas-strong, #eef);
        border-color: var(--s-line, rgba(15, 30, 55, 0.08));
      }
      .cell .label {
        font-size: 0.72rem;
        color: var(--s-ink-soft, #666);
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .cell .val {
        font-size: 1.1rem;
        font-weight: 700;
        font-family: var(--font-display, inherit);
        margin-top: 2px;
      }
      .variance-pos {
        color: var(--s-success, rgb(15, 110, 60));
      }
      .variance-neg {
        color: var(--s-danger, rgb(160, 25, 25));
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-variant-numeric: tabular-nums;
        font-size: 0.9rem;
      }
      th,
      td {
        padding: 7px 8px;
        border-bottom: 1px solid var(--s-line, #eee);
        text-align: left;
      }
      th {
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--s-ink-soft, #666);
      }
      td.right,
      th.right {
        text-align: right;
      }
      .reconcile-row {
        margin-top: 12px;
        padding: 10px 14px;
        border-radius: 12px;
        font-size: 0.85rem;
        line-height: 1.45;
        display: flex;
        align-items: flex-start;
        gap: 10px;
      }
      .reconcile-row sl-icon {
        flex: none;
        font-size: 18px;
        margin-top: 1px;
      }
      .reconcile-row.ok {
        background: var(--s-success-soft, rgba(0, 160, 80, 0.08));
        color: var(--s-success, rgb(15, 110, 60));
      }
      .reconcile-row.drift {
        background: var(--s-danger-soft, rgba(220, 30, 30, 0.08));
        color: var(--s-danger, rgb(160, 25, 25));
      }
      .back-link {
        margin-bottom: 8px;
      }
      .print-actions {
        margin-top: 14px;
      }
      @media print {
        :host {
          padding: 0;
          max-width: none;
          color: #000;
        }
        .back-link,
        .print-actions,
        sl-button {
          display: none !important;
        }
        .icon-tile {
          display: none;
        }
        sl-card::part(base) {
          box-shadow: none;
          border: 1px solid #ccc;
        }
        .cell {
          background: #f5f5f5 !important;
          border-color: #ddd !important;
        }
        .reconcile-row {
          border: 1px solid #ccc;
          background: #fff !important;
          color: #000 !important;
        }
      }
    `,
  ];

  private _loadedKey: string | null = null;

  // (Re)load from updated() keyed on shop + session id: load when the activeShop
  // context first arrives (absent on a hard nav straight to this URL) or when the
  // shop / session changes. The re-trigger deliberately does NOT gate on
  // _loading — that trap left the page stuck on "Loading…" forever once the shop
  // context arrived after the first paint.
  override updated(): void {
    const shop = this.activeShop?.shopName ?? null;
    if (!shop) return;
    const id = (appRouter.context.params as Record<string, string | undefined>)
      .id;
    const key = `${shop}|${id ?? ''}`;
    if (key !== this._loadedKey) {
      this._loadedKey = key;
      void this._load();
    }
  }

  // Always resolves to data / "No data" / an error — never an indefinite
  // spinner: _loading is cleared in the finally on every path.
  private async _load(): Promise<void> {
    const id = (appRouter.context.params as Record<string, string | undefined>)
      .id;
    this._loading = true;
    this._error = null;
    try {
      if (!this.activeShop) {
        this._error = 'No active shop.';
        return;
      }
      if (!id) {
        this._error = 'Missing session id.';
        return;
      }
      const api = ApiFactory.authenticated();
      const r = await api.getZReport(this.activeShop.shopName, id);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Could not load Z-report.';
        return;
      }
      this._report = r.data;
    } finally {
      this._loading = false;
    }
  }

  private _print() {
    window.print();
  }

  private _back() {
    history.length > 1
      ? history.back()
      : (window.location.pathname = '/cash/sessions');
  }

  private _renderBalances(z: IZReport) {
    const variance = z.variance ?? null;
    const varianceClass =
      variance === null
        ? ''
        : variance > 0
          ? 'variance-pos'
          : variance < 0
            ? 'variance-neg'
            : '';
    return html`
      <div class="balance-grid">
        <div class="cell">
          <div class="label">Opening float</div>
          <div class="val">${z.openingFloat.toFixed(2)}</div>
        </div>
        <div class="cell">
          <div class="label">Cash sales</div>
          <div class="val">${z.movementsSaleTotal.toFixed(2)}</div>
        </div>
        <div class="cell">
          <div class="label">Cash refunds</div>
          <div class="val">${z.movementsRefundTotal.toFixed(2)}</div>
        </div>
        <div class="cell">
          <div class="label">Pay-ins</div>
          <div class="val">${z.movementsPayInTotal.toFixed(2)}</div>
        </div>
        <div class="cell">
          <div class="label">Pay-outs</div>
          <div class="val">${z.movementsPayOutTotal.toFixed(2)}</div>
        </div>
        <div class="cell">
          <div class="label">Adjustments</div>
          <div class="val">${z.movementsAdjustmentTotal.toFixed(2)}</div>
        </div>
        <div class="cell strong">
          <div class="label">Expected close</div>
          <div class="val">${z.expectedClose.toFixed(2)}</div>
        </div>
        <div class="cell strong">
          <div class="label">Counted close</div>
          <div class="val">
            ${z.countedClose != null ? z.countedClose.toFixed(2) : '—'}
          </div>
        </div>
        <div class="cell strong">
          <div class="label">Variance</div>
          <div class="val ${varianceClass}">
            ${
              variance != null
                ? `${variance > 0 ? '+' : ''}${variance.toFixed(2)}`
                : '—'
            }
          </div>
        </div>
      </div>
    `;
  }

  render() {
    if (!this.activeShop) return html`<p>No active shop.</p>`;
    if (this._loading) return html`<p>Loading...</p>`;
    if (this._error) {
      return html`<sl-alert variant="danger" open>${this._error}</sl-alert>`;
    }
    const z = this._report;
    if (!z) return html`<p>No data.</p>`;

    const opened = new Date(z.openedUtc);
    const closed = z.closedUtc ? new Date(z.closedUtc) : null;
    const r = z.reconcile;
    const cleanReconcile =
      r.drift === 0 &&
      r.missingSaleIds.length === 0 &&
      r.missingRefundIds.length === 0 &&
      r.orphanMovementIds.length === 0;

    return html`
      <div class="back-link">
        <sl-button size="small" variant="text" @click=${this._back}
          >← Back</sl-button
        >
      </div>
      <div class="report-head">
        <span class="icon-tile">
          <sl-icon name="receipt"></sl-icon>
        </span>
        <div>
          <h1 class="page-head__title">Z-Report</h1>
          <p class="sub">
            Session opened
            ${opened.toLocaleString()}${
              closed
                ? html` · closed ${closed.toLocaleString()}`
                : ' · still open'
            }
            · device <code>${z.deviceId}</code>
            ${
              z.session.status === 'open'
                ? html`<sl-badge variant="success" pill>Open</sl-badge>`
                : html`<sl-badge variant="neutral" pill>Closed</sl-badge>`
            }
          </p>
        </div>
      </div>

      <sl-card class="panel">
        <h3>Balances</h3>
        ${this._renderBalances(z)}

        <div class="reconcile-row ${cleanReconcile ? 'ok' : 'drift'}">
          <sl-icon
            name=${cleanReconcile ? 'check-circle-fill' : 'exclamation-triangle-fill'}
          ></sl-icon>
          <span>
            ${
              cleanReconcile
                ? html`Fully reconciled — sales total
                  ${r.salesTotalForSession.toFixed(2)}, refunds total
                  ${r.refundsTotalForSession.toFixed(2)}, drift 0.`
                : html`Drift detected: sales
                  ${r.salesTotalForSession.toFixed(2)}/${r.movementsSaleTotal.toFixed(2)},
                  refunds
                  ${r.refundsTotalForSession.toFixed(2)}/${r.movementsRefundTotal.toFixed(2)},
                  delta ${r.drift.toFixed(2)}.
                  ${
                    r.missingSaleIds.length > 0
                      ? html`${r.missingSaleIds.length} missing sale.`
                      : nothing
                  }
                  ${
                    r.missingRefundIds.length > 0
                      ? html`${r.missingRefundIds.length} missing refund.`
                      : nothing
                  }`
            }
          </span>
        </div>

        ${
          z.session.status === 'closed'
            ? html`<div class="print-actions">
                <button class="btn-secondary-pill" @click=${this._print}>
                  <sl-icon name="printer"></sl-icon>
                  Print Z-report
                </button>
              </div>`
            : nothing
        }
      </sl-card>

      <sl-card class="panel">
        <h3>Sales (${z.sales.length})</h3>
        ${
          z.sales.length === 0
            ? html`<p class="sub">No sales attributed to this session.</p>`
            : html`<table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Time</th>
                    <th>Method</th>
                    <th class="right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${z.sales.map(
                    (s) => html`
                      <tr>
                        <td>${s.daySequence || '—'}</td>
                        <td>${new Date(s.createdUtc).toLocaleTimeString()}</td>
                        <td>
                          ${s.paymentMethod}
                          ${
                            s.isRefunded
                              ? html`<sl-badge variant="warning"
                                  >Refunded</sl-badge
                                >`
                              : nothing
                          }
                        </td>
                        <td class="right">${s.total.toFixed(2)}</td>
                      </tr>
                    `
                  )}
                </tbody>
              </table>`
        }
      </sl-card>

      <sl-card class="panel">
        <h3>Refunds (${z.refunds.length})</h3>
        ${
          z.refunds.length === 0
            ? html`<p class="sub">No refunds during this session.</p>`
            : html`<table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Sale</th>
                    <th>Method</th>
                    <th class="right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${z.refunds.map(
                    (r) => html`
                      <tr>
                        <td>${new Date(r.createdUtc).toLocaleTimeString()}</td>
                        <td><code>${r.refSaleId.slice(0, 8)}…</code></td>
                        <td>${r.refundMethod}</td>
                        <td class="right">${r.amount.toFixed(2)}</td>
                      </tr>
                    `
                  )}
                </tbody>
              </table>`
        }
      </sl-card>

      <sl-card class="panel">
        <h3>Movements summary</h3>
        ${
          z.movementSummary.length === 0
            ? html`<p class="sub">No movements yet.</p>`
            : html`<table>
                <thead>
                  <tr>
                    <th>Kind</th>
                    <th class="right">Count</th>
                    <th class="right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${z.movementSummary.map(
                    (m) => html`
                      <tr>
                        <td>${m.kind}</td>
                        <td class="right">${m.count}</td>
                        <td class="right">${m.total.toFixed(2)}</td>
                      </tr>
                    `
                  )}
                </tbody>
              </table>`
        }
      </sl-card>
    `;
  }
}
